// ─── ImportGuiasDialog — importar guias → criar (e despachar) OTs (Casais) ──
// O operador escolhe os ficheiros das guias; o planner determina a obra (6 díg
// do nome), motorista/viatura/tipo (do conteúdo ou do HABITUAL da obra), mostra
// uma pré-visualização com exceções "por rever", aplica a regra do mesmo veículo
// e, ao confirmar, cria as OTs de entrega e (opcional) despacha a rota em fundo.
// Separador "Histórico" lista as OTs já criadas por importação.

import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button,
  IconButton, CircularProgress, TextField, MenuItem, Switch, Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import DescriptionIcon from '@material-ui/icons/Description';
import HistoryIcon from '@material-ui/icons/History';
import RoomIcon from '@material-ui/icons/Room';
import EventIcon from '@material-ui/icons/Event';
import SendIcon from '@material-ui/icons/Send';
import { AuthContext } from '../../App';
import { saveRouteToAPI } from '../../store/slices/routesSlice';
import { calculateRouteGoogle } from '../../utils/googleRoutesApi';
import { findBestOrder } from '../../utils/consolidationValidator';

const C = {
  primary: '#0369a1', primaryDark: '#075985', primarySoft: '#e0f2fe',
  ink: '#0f172a', dim: '#64748b', muted: '#94a3b8', line: '#e8edf3', card: '#ffffff', bg: '#f8fafc',
};

const useStyles = makeStyles(() => ({
  paper: { borderRadius: 16, overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', gap: 13, padding: '18px 22px', background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 60%)', borderBottom: `1px solid ${C.line}` },
  headerIcon: { width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg, ${C.primary}, #0ea5e9)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0, boxShadow: '0 6px 16px rgba(3,105,161,0.26)' },
  title: { fontSize: '1.12rem', fontWeight: 800, color: C.ink, letterSpacing: '-0.02em', lineHeight: 1.15 },
  subtitle: { fontSize: '0.74rem', color: C.dim, marginTop: 1 },
  tabs: { display: 'inline-flex', gap: 3, padding: 3, borderRadius: 999, background: '#eef2f7', marginBottom: 16 },
  tab: { cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700, color: C.dim, transition: 'all 0.15s', userSelect: 'none' },
  tabOn: { background: C.primary, color: '#fff', boxShadow: '0 4px 12px rgba(3,105,161,0.22)' },

  settings: { display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', padding: '14px 16px', background: C.bg, border: `1px solid ${C.line}`, borderRadius: 12, marginBottom: 16 },
  toggleWrap: { display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto', padding: '2px 4px 2px 12px', border: `1px solid ${C.line}`, borderRadius: 999, background: '#fff' },

  drop: { border: `2px dashed ${C.line}`, borderRadius: 14, padding: '30px 16px', textAlign: 'center', cursor: 'pointer', background: '#fff', transition: 'all 0.18s', '&:hover': { borderColor: C.primary, background: '#f0f9ff' } },
  dropActive: { borderColor: C.primary, background: '#e0f2fe', transform: 'scale(1.005)' },
  dropIcon: { width: 52, height: 52, borderRadius: '50%', background: C.primarySoft, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 },

  summaryBar: { display: 'flex', gap: 8, marginTop: 16, marginBottom: 8, flexWrap: 'wrap' },
  stat: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 700, border: '1px solid transparent' },

  tableWrap: { border: `1px solid ${C.line}`, borderRadius: 12, overflow: 'hidden', marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { fontSize: '0.6rem', fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '9px 12px', textAlign: 'left', background: '#f8fafc', borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap' },
  td: { fontSize: '0.78rem', color: C.ink, padding: '9px 12px', borderBottom: `1px solid #f1f5f9`, verticalAlign: 'middle' },
  pill: { display: 'inline-block', fontSize: '0.6rem', fontWeight: 800, borderRadius: 999, padding: '2px 9px', textTransform: 'uppercase', letterSpacing: '0.03em' },

  banner: { padding: '12px 16px', borderRadius: 12, marginBottom: 12, fontSize: '0.8rem' },
  resultCard: { border: `1px solid ${C.line}`, borderRadius: 14, padding: 18, marginBottom: 14 },
  bigNum: { fontSize: '2rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em' },

  footer: { padding: '12px 20px', borderTop: `1px solid ${C.line}`, gap: 10 },
  primaryBtn: { background: C.primary, color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: 10, padding: '7px 18px', boxShadow: '0 4px 14px rgba(3,105,161,0.22)', '&:hover': { background: C.primaryDark } },
  primaryBtnOff: { background: '#cbd5e1', color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: 10, padding: '7px 18px', boxShadow: 'none' },
  ghostBtn: { textTransform: 'none', fontWeight: 600, color: C.dim, borderRadius: 10 },
}));

const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
// Data de execução por omissão: criação + 3 dias úteis (regra Casais). Só depois
// dessa data as OT importadas são consideradas atrasadas.
function addBusinessDays(date, n) {
  const d = new Date(date);
  let added = 0;
  while (added < n) { d.setDate(d.getDate() + 1); const wd = d.getDay(); if (wd !== 0 && wd !== 6) added++; }
  return d;
}

function ImportGuiasDialog({ open, onClose, onImported, lang = 'pt' }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientSettings } = useContext(AuthContext);
  const pt = lang === 'pt';

  const [obras, setObras] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rows, setRows] = useState([]);        // linhas resolvidas
  const [sedeId, setSedeId] = useState('');
  const [serviceDate, setServiceDate] = useState(addBusinessDays(new Date(), 3).toISOString().slice(0, 10));
  // Novo fluxo Casais: PDF → só cria as OTs. O despacho (veículo/motorista/rota)
  // é manual depois. Auto-despacho desligado por defeito.
  const [autoDispatchOn, setAutoDispatchOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dispatching, setDispatching] = useState(false);
  const [dispatchResult, setDispatchResult] = useState(null);
  const [dispatchError, setDispatchError] = useState(null);
  const [mode, setMode] = useState('import'); // 'import' | 'history'
  const [history, setHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const h = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!open || !token) return;
    setLoading(true); setRows([]); setResult(null); setError(null);
    setDispatching(false); setDispatchResult(null); setDispatchError(null);
    Promise.all([
      fetch('/planner-api/obras', { headers: h }).then(r => r.ok ? r.json() : []),
      fetch('/planner-api/drivers', { headers: h }).then(r => r.ok ? r.json() : []),
      fetch('/planner-api/vehicles?includeDisabled=true', { headers: h }).then(r => r.ok ? r.json() : []),
      fetch('/planner-api/transport-types', { headers: h }).then(r => r.ok ? r.json() : []),
      fetch('/planner-api/locations', { headers: h }).then(r => r.ok ? r.json() : []),
    ]).then(([ob, dr, ve, ty, lo]) => {
      setObras(ob || []); setDrivers(dr || []); setVehicles(ve || []); setTypes(ty || []); setLocations(lo || []);
      // Resolver sede: defaultDeparture 'favorite:<id>' → isDeparture → nome com "sede".
      const dep = clientSettings?.defaultDepartureByOrderType?.delivery;
      let pick = null;
      if (dep && String(dep).startsWith('favorite:')) pick = (lo || []).find(l => String(l.id) === String(dep).split(':')[1]);
      if (!pick) pick = (lo || []).find(l => l.isDeparture);
      if (!pick) pick = (lo || []).find(l => norm(l.name).includes('sede'));
      if (pick) setSedeId(String(pick.id));
    }).catch(() => setError(pt ? 'Falha a carregar dados.' : 'Load failed.')).finally(() => setLoading(false));
  }, [open, token, h, clientSettings, pt]);

  // Novo fluxo (PDF → só OTs): o backend (/parse) lê os campos da guia
  //   · guiaNumber (Nº do Documento) → nº da OT
  //   · obraCode (destinatário, 6 díg) → destino
  //   · codigoAT (quando existe)
  // Aqui só resolvemos a obra pelo código e validamos. Sem motorista/viatura/
  // rota — isso é manual depois. Obra não registada → erro (salta e avisa).
  const resolveParsedRow = useCallback((res) => {
    const obra = obras.find(o => String(o.codigoObra) === String(res.obraCode)) || null;
    let status = 'ok', reason = '';
    if (res.error) { status = 'erro'; reason = res.error; }
    else if (!obra) { status = 'erro'; reason = pt ? `Obra ${res.obraCode || '?'} não registada` : 'Work not registered'; }
    else if (obra.lat == null || obra.lng == null) { status = 'erro'; reason = pt ? `Obra ${obra.codigoObra} sem coordenadas` : 'No coords'; }
    return {
      fileName: res.fileName,
      obraCode: res.obraCode || null,
      guiaNumber: res.guiaNumber || null,
      guiaRef: res.guiaNumber || res.fileName,
      orderNumber: res.guiaNumber || null,
      codigoAT: res.codigoAT || null,
      tipo: res.tipo || null,
      obraId: obra?.id || null,
      obraName: obra?.name || null,
      // só-OT — sem motorista/viatura
      driverExternalId: null, driverName: null, vehicleDeviceId: null, vehiclePlate: null,
      status, reason,
    };
  }, [obras, pt]);

  async function handleFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setLoading(true); setError(null);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const r = await fetch('/planner-api/casais-import/parse', { method: 'POST', headers: h, body: fd });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || (pt ? 'Falha a ler as guias.' : 'Parse failed.'));
      const parsed = (data.results || []).map(resolveParsedRow);
      setRows(prev => [...prev, ...parsed]);
      setResult(null);
    } catch (e) {
      setError(e.message || (pt ? 'Falha a ler as guias.' : 'Parse failed.'));
    } finally { setLoading(false); }
  }

  const loadHistory = useCallback(() => {
    setHistLoading(true);
    fetch('/planner-api/casais-import/history?limit=150', { headers: h })
      .then(r => r.ok ? r.json() : { rows: [] })
      .then(d => setHistory(d.rows || []))
      .catch(() => setHistory([]))
      .finally(() => setHistLoading(false));
  }, [h]);

  useEffect(() => { if (open && mode === 'history' && history === null) loadHistory(); }, [open, mode, history, loadHistory]);
  useEffect(() => { if (open) { setMode('import'); setHistory(null); } }, [open]);

  const okRows = rows.filter(r => r.status === 'ok');
  const reverRows = rows.filter(r => r.status === 'rever');
  const erroRows = rows.filter(r => r.status === 'erro');
  const distinctVehicles = useMemo(() => Array.from(new Set(okRows.map(r => r.vehiclePlate).filter(Boolean))), [okRows]);
  const mixedVehicles = distinctVehicles.length > 1;
  const sede = locations.find(l => String(l.id) === String(sedeId));

  // Fase B — auto-despacho EM FUNDO: a partir das OTs criadas (todas do mesmo
  // veículo, garantido pela regra acima), constrói a rota sede→obras, calcula
  // a geometria/tempo client-side (Google Routes) e despacha (saveRouteToAPI
  // → cria routeDriver e chega à app). Depois grava horas (tempo por troço) e
  // valor (horas × valor/hora da viatura) nas linhas de transporte de cada OT.
  async function autoDispatch(created, doDispatch = true) {
    // Paragens a partir das OTs criadas; filtra as que têm coordenadas.
    const stops = (created || [])
      .filter(c => c.lat != null && c.lng != null)
      .map(c => ({ otId: c.id, orderNumber: c.orderNumber, lat: Number(c.lat), lng: Number(c.lng), name: c.deliveryName || `Obra ${c.obra}`, tipo: c.tipo || null }));
    if (!stops.length) throw new Error(pt ? 'As OTs criadas não têm coordenadas de obra.' : 'No coords.');
    if (!sede || sede.latitude == null || sede.longitude == null) throw new Error(pt ? 'Sem coordenadas da sede (origem).' : 'No origin coords.');

    // Otimiza a ORDEM das paragens com o MESMO otimizador do planner
    // (findBestOrder: força bruta ≤6 paragens, NN+2-opt acima), arrancando na
    // sede. Antes a rota seguia a ordem dos ficheiros e podia ziguezaguear.
    if (stops.length > 1) {
      try {
        const sLat = Number(sede.latitude), sLng = Number(sede.longitude);
        const objs = stops.map(s => ({ id: s.otId, deliveryLat: s.lat, deliveryLng: s.lng, pickupLat: sLat, pickupLng: sLng }));
        const best = findBestOrder(objs, { driverHome: { lat: sLat, lng: sLng } });
        const ord = Array.isArray(best?.reorderedOrders) ? best.reorderedOrders : null;
        if (ord && ord.length === stops.length) {
          const pos = new Map(ord.map((o, i) => [o.id, i]));
          stops.sort((a, b) => (pos.get(a.otId) ?? 0) - (pos.get(b.otId) ?? 0));
        }
      } catch { /* mantém ordem de criação se o otimizador falhar */ }
    }

    const origin = [Number(sede.longitude), Number(sede.latitude)];
    const last = stops[stops.length - 1];
    const mids = stops.slice(0, -1);
    const midCoords = mids.map(s => [s.lng, s.lat]);
    const destCoords = [last.lng, last.lat];

    // Cálculo headless (geometria + duração + troços). Google só faz carro; a
    // ETA de pesado pode ser afinada no futuro (distância ÷ velocidade média).
    const regionCode = (clientSettings?.defaultCountry || 'PT').toUpperCase();
    const calc = await calculateRouteGoogle(origin, destCoords, midCoords, { regionCode });
    const computed = calc.route;

    const veh = created.find(c => c.vehicleId) || created[0] || {};
    const drv = created.find(c => c.driverId) || created[0] || {};
    const plate = veh.vehiclePlate || '—';
    const dateForName = new Date(serviceDate || Date.now()).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' });
    const n = stops.length;

    const route = {
      name: `${plate} · ${dateForName} (${n} ${n === 1 ? 'ordem' : 'ordens'})`,
      cargoRef: stops.map(s => s.tipo).filter(Boolean).join(' + ') || null,
      origin: { name: sede.name || 'Sede', coordinates: origin },
      waypoints: mids.map(s => ({ name: s.name, coordinates: [s.lng, s.lat], stopDuration: 30, otId: s.otId, orderNumber: s.orderNumber, waypointKind: 'delivery' })),
      destination: { name: last.name, coordinates: destCoords, stopDuration: 30, otId: last.otId, orderNumber: last.orderNumber, waypointKind: 'delivery' },
      computedRoute: computed,
      provider: 'google',
      vehicleId: veh.vehicleId || null,
      vehiclePlate: veh.vehiclePlate || null,
      driverId: drv.driverId || null,
      driverName: drv.driverName || null,
      sourceOTIds: stops.map(s => s.otId),
    };

    const saved = await dispatch(saveRouteToAPI({ token, route, dispatch: doDispatch })).unwrap();

    // horas por OT = duração do troço que chega a essa paragem (legs[i]).
    const legs = Array.isArray(computed?.legs) ? computed.legs : [];
    const items = stops.map((s, i) => ({ otId: s.otId, horas: legs[i]?.duration != null ? legs[i].duration / 3600 : null }));
    await fetch('/planner-api/casais-import/set-lines', { method: 'POST', headers: { ...h, 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) })
      .then(r => r.ok ? r.json() : null).catch(() => null);

    return {
      routeId: saved?.id || null,
      routeName: route.name,
      plate,
      dispatched: doDispatch,
      distanceKm: computed?.distance != null ? computed.distance / 1000 : null,
      durationH: computed?.duration != null ? computed.duration / 3600 : null,
      stops: stops.map((s, i) => ({ ...s, horas: items[i].horas })),
    };
  }

  async function doImport() {
    if (!okRows.length) return;
    if (mixedVehicles) { setError(pt ? `Os ficheiros têm veículos diferentes (${distinctVehicles.join(', ')}). Seleciona ficheiros do mesmo veículo.` : 'Mixed vehicles.'); return; }
    setCreating(true); setError(null); setDispatchError(null); setDispatchResult(null);
    try {
      const body = {
        rows: okRows.map(r => ({ obraId: r.obraId, orderNumber: r.orderNumber, codigoAT: r.codigoAT, guiaRef: r.guiaRef, vehicleDeviceId: r.vehicleDeviceId, vehiclePlate: r.vehiclePlate, driverExternalId: r.driverExternalId, driverName: r.driverName, tipo: r.tipo })),
        sede: sede ? { name: sede.name, lat: sede.latitude, lng: sede.longitude } : {},
        serviceDate,
      };
      const r = await fetch('/planner-api/casais-import/create', { method: 'POST', headers: { ...h, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      const data = await r.json();
      setResult(data);
      setCreating(false);

      // Fase B — despacho automático em fundo das OTs recém-criadas.
      if ((data.created || []).length) {
        setDispatching(true);
        try {
          const disp = await autoDispatch(data.created, autoDispatchOn);
          setDispatchResult(disp);
        } catch (e) {
          setDispatchError(e.message || (pt ? 'Falha ao despachar automaticamente. As OTs ficaram criadas — podes despachar no planeamento.' : 'Auto-dispatch failed.'));
        } finally { setDispatching(false); }
      }
      if (onImported) onImported(data);
    } catch (e) { setError(e.message || 'Erro ao importar.'); setCreating(false); }
  }

  const STATUS = {
    ok:    { bg: '#dcfce7', fg: '#166534', bd: '#bbf7d0', label: 'OK', Icon: CheckCircleIcon, ic: '#16a34a' },
    rever: { bg: '#fef9c3', fg: '#854d0e', bd: '#fde68a', label: pt ? 'por rever' : 'review', Icon: WarningIcon, ic: '#ca8a04' },
    erro:  { bg: '#fee2e2', fg: '#991b1b', bd: '#fecaca', label: pt ? 'erro' : 'error', Icon: ErrorIcon, ic: '#dc2626' },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth classes={{ paper: classes.paper }}>
      {/* Cabeçalho */}
      <Box className={classes.header}>
        <Box className={classes.headerIcon}><DescriptionIcon /></Box>
        <Box style={{ minWidth: 0 }}>
          <Typography className={classes.title}>{pt ? 'Importar Guias → Ordens de Transporte' : 'Import guias → Orders'}</Typography>
          <Typography className={classes.subtitle}>{pt ? 'Cria e despacha as OTs automaticamente a partir dos ficheiros das guias' : 'Create and dispatch OTs from guia files'}</Typography>
        </Box>
        <Box style={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      <DialogContent style={{ padding: '18px 22px' }}>
        {/* Separadores */}
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <Box className={classes.tabs}>
            <Box className={`${classes.tab} ${mode === 'import' ? classes.tabOn : ''}`} onClick={() => setMode('import')}>
              <CloudUploadIcon style={{ fontSize: 15 }} /> {pt ? 'Importar' : 'Import'}
            </Box>
            <Box className={`${classes.tab} ${mode === 'history' ? classes.tabOn : ''}`} onClick={() => setMode('history')}>
              <HistoryIcon style={{ fontSize: 15 }} /> {pt ? 'Histórico' : 'History'}
            </Box>
          </Box>
          <Box style={{ flex: 1 }} />
          {mode === 'history' && (
            <Button size="small" onClick={loadHistory} startIcon={<HistoryIcon style={{ fontSize: 15 }} />} className={classes.ghostBtn}>
              {pt ? 'Actualizar' : 'Refresh'}
            </Button>
          )}
        </Box>

        {/* ─── HISTÓRICO ─── */}
        {mode === 'history' ? (
          histLoading ? (
            <Box style={{ textAlign: 'center', padding: 40 }}><CircularProgress /></Box>
          ) : !history || history.length === 0 ? (
            <Box style={{ textAlign: 'center', padding: '40px 20px', color: C.muted }}>
              <HistoryIcon style={{ fontSize: 40, opacity: 0.4 }} />
              <Typography style={{ fontSize: '0.86rem', marginTop: 8 }}>{pt ? 'Ainda não há OTs criadas por importação.' : 'No imported OTs yet.'}</Typography>
            </Box>
          ) : (
            <Box className={classes.tableWrap} style={{ overflowX: 'auto' }}>
              <table className={classes.table}>
                <thead><tr>
                  <th className={classes.th}>{pt ? 'Nº OT' : 'OT'}</th>
                  <th className={classes.th}>{pt ? 'Guia' : 'Guia'}</th>
                  <th className={classes.th}>{pt ? 'Obra' : 'Work'}</th>
                  <th className={classes.th}>{pt ? 'Viatura' : 'Vehicle'}</th>
                  <th className={classes.th}>{pt ? 'Tipo' : 'Type'}</th>
                  <th className={classes.th} style={{ textAlign: 'right' }}>h</th>
                  <th className={classes.th} style={{ textAlign: 'right' }}>€</th>
                  <th className={classes.th}>{pt ? 'Rota' : 'Route'}</th>
                  <th className={classes.th}>{pt ? 'Estado' : 'Status'}</th>
                </tr></thead>
                <tbody>
                  {history.map(r => (
                    <tr key={r.id}>
                      <td className={classes.td} style={{ fontWeight: 800 }}>{r.orderNumber}</td>
                      <td className={classes.td} style={{ fontSize: '0.7rem', color: C.dim }}>{r.guiaRef || '—'}</td>
                      <td className={classes.td}>{r.obraCodigo || '?'}{r.obraNome ? ` · ${r.obraNome}` : ''}</td>
                      <td className={classes.td}>{r.vehiclePlate || '—'}</td>
                      <td className={classes.td}>{r.tipo || '—'}</td>
                      <td className={classes.td} style={{ textAlign: 'right' }}>{r.horas != null ? Number(r.horas).toFixed(2) : '—'}</td>
                      <td className={classes.td} style={{ textAlign: 'right', fontWeight: 700 }}>{r.valor != null ? `${Number(r.valor).toFixed(2)} €` : '—'}</td>
                      <td className={classes.td} style={{ fontSize: '0.72rem' }}>
                        {r.routeId
                          ? (r.routeSent
                            ? <span style={{ color: '#1d4ed8', fontWeight: 700 }}>✈ {pt ? 'enviada' : 'sent'}</span>
                            : <span style={{ color: '#a16207', fontWeight: 700 }}>📝 {pt ? 'rascunho' : 'draft'}</span>)
                          : <span style={{ color: C.muted }}>{pt ? '— sem rota' : 'no route'}</span>}
                      </td>
                      <td className={classes.td}><span className={classes.pill} style={{ background: C.primarySoft, color: '#075985' }}>{r.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )

        /* ─── A CARREGAR ─── */
        ) : loading ? (
          <Box style={{ textAlign: 'center', padding: 44 }}><CircularProgress /></Box>

        /* ─── RESULTADO ─── */
        ) : result ? (
          <Box>
            <Box className={classes.resultCard} style={{ background: '#f0fdf4', borderColor: '#bbf7d0', display: 'flex', alignItems: 'center', gap: 16 }}>
              <Box style={{ width: 54, height: 54, borderRadius: 14, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CheckCircleIcon style={{ color: '#16a34a', fontSize: 30 }} />
              </Box>
              <Box>
                <Typography className={classes.bigNum} style={{ color: '#166534' }}>{result.created?.length || 0}</Typography>
                <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#166534' }}>{pt ? 'OTs criadas' : 'OTs created'}</Typography>
              </Box>
              <Box style={{ flex: 1 }} />
              <Box style={{ maxHeight: 70, overflowY: 'auto', textAlign: 'right' }}>
                {(result.created || []).map(c => (
                  <Typography key={c.id} style={{ fontSize: '0.72rem', color: '#15803d' }}>{c.orderNumber} · obra {c.obra}</Typography>
                ))}
              </Box>
            </Box>

            {(result.errors || []).length > 0 && (
              <Box className={classes.banner} style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
                <Typography style={{ fontWeight: 700, color: '#991b1b', marginBottom: 2 }}>{result.errors.length} {pt ? 'por rever / erros' : 'errors'}</Typography>
                {result.errors.map((e, i) => <Typography key={i} style={{ fontSize: '0.76rem', color: '#991b1b' }}>• {e.guiaRef || '—'}: {e.error}</Typography>)}
              </Box>
            )}

            {/* Fase B — despacho automático */}
            {dispatching && (
              <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 12 }}>
                <CircularProgress size={20} />
                <Typography style={{ fontSize: '0.86rem', color: C.primary, fontWeight: 700 }}>{pt ? 'A calcular a melhor rota e a despachar…' : 'Calculating route and dispatching…'}</Typography>
              </Box>
            )}
            {dispatchResult && (
              <Box className={classes.resultCard} style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <Box style={{ width: 36, height: 36, borderRadius: 10, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <LocalShippingIcon style={{ color: '#1d4ed8', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography style={{ fontWeight: 800, color: '#1e3a8a', fontSize: '0.9rem' }}>
                      {dispatchResult.dispatched ? (pt ? 'Rota despachada para a app' : 'Route dispatched') : (pt ? 'Rota criada (rascunho)' : 'Route created (draft)')}
                    </Typography>
                    <Typography style={{ fontSize: '0.74rem', color: '#1e40af' }}>{dispatchResult.routeName}</Typography>
                  </Box>
                  <Box style={{ flex: 1 }} />
                  <Box style={{ textAlign: 'right' }}>
                    {dispatchResult.distanceKm != null && <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#1e3a8a' }}>{dispatchResult.distanceKm.toFixed(1)} km</Typography>}
                    {dispatchResult.durationH != null && <Typography style={{ fontSize: '0.72rem', color: '#1e40af' }}>{dispatchResult.durationH.toFixed(2)} h · {dispatchResult.plate}</Typography>}
                  </Box>
                </Box>
                <Box style={{ borderTop: '1px solid #bfdbfe', paddingTop: 8 }}>
                  {(dispatchResult.stops || []).map((s, i) => (
                    <Box key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', color: '#334155', padding: '2px 0' }}>
                      <span><b style={{ color: '#1e3a8a' }}>{i + 1}.</b> {s.orderNumber} · {s.name}</span>
                      {s.horas != null && <span style={{ color: C.dim }}>{s.horas.toFixed(2)} h</span>}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {dispatchError && (
              <Box className={classes.banner} style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                <Typography style={{ fontWeight: 700, color: '#92400e' }}>⚠ {pt ? 'Despacho automático falhou' : 'Auto-dispatch failed'}</Typography>
                <Typography style={{ fontSize: '0.76rem', color: '#92400e' }}>{dispatchError}</Typography>
                <Typography style={{ fontSize: '0.72rem', color: '#a16207', marginTop: 4 }}>{pt ? 'As OTs foram criadas — podes despachá-las no planeamento.' : 'OTs created; dispatch manually.'}</Typography>
              </Box>
            )}
          </Box>

        /* ─── IMPORTAR (formulário) ─── */
        ) : (
          <>
            {error && (
              <Box className={classes.banner} style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', display: 'flex', alignItems: 'center', gap: 8 }}>
                <ErrorIcon style={{ fontSize: 18 }} /> {error}
              </Box>
            )}

            {/* Definições: origem · data · despacho */}
            <Box className={classes.settings}>
              <TextField select size="small" variant="outlined" label={pt ? 'Origem (sede)' : 'Origin'} value={sedeId} onChange={e => setSedeId(e.target.value)} style={{ minWidth: 240 }}
                InputProps={{ startAdornment: <RoomIcon style={{ fontSize: 17, color: C.muted, marginRight: 6 }} /> }}>
                {locations.map(l => <MenuItem key={l.id} value={String(l.id)}>{l.name}</MenuItem>)}
              </TextField>
              <TextField size="small" variant="outlined" type="date" label={pt ? 'Data do serviço' : 'Date'} InputLabelProps={{ shrink: true }} value={serviceDate} onChange={e => setServiceDate(e.target.value)}
                InputProps={{ startAdornment: <EventIcon style={{ fontSize: 17, color: C.muted, marginRight: 6 }} /> }} />
              <Tooltip title={pt ? 'Se ligado, a rota é enviada logo para a app do motorista. Se desligado, fica em rascunho para despachares depois.' : 'Dispatch to driver app'}>
                <Box className={classes.toggleWrap}>
                  <SendIcon style={{ fontSize: 15, color: autoDispatchOn ? C.primary : C.muted }} />
                  <span style={{ fontSize: '0.76rem', fontWeight: 600, color: autoDispatchOn ? C.ink : C.dim }}>{pt ? 'Despachar p/ app' : 'Dispatch'}</span>
                  <Switch size="small" color="primary" checked={autoDispatchOn} onChange={e => setAutoDispatchOn(e.target.checked)} />
                </Box>
              </Tooltip>
            </Box>

            {/* Zona de drop */}
            <label>
              <input type="file" multiple hidden accept=".pdf,.txt" onChange={e => handleFiles(e.target.files)} />
              <Box
                className={`${classes.drop} ${dragOver ? classes.dropActive : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
              >
                <Box className={classes.dropIcon}><CloudUploadIcon style={{ fontSize: 28, color: C.primary }} /></Box>
                <Typography style={{ fontWeight: 800, color: C.ink, fontSize: '0.92rem' }}>{pt ? 'Arrasta os ficheiros das guias para aqui' : 'Drop guia files here'}</Typography>
                <Typography style={{ fontSize: '0.78rem', color: C.dim, marginTop: 2 }}>{pt ? 'ou clica para escolher' : 'or click to choose'}</Typography>
                <Typography style={{ fontSize: '0.7rem', color: C.muted, marginTop: 6 }}>{pt ? 'O código de obra (6 dígitos) é lido do nome do ficheiro' : 'Work code (6 digits) from the file name'}</Typography>
              </Box>
            </label>

            {mixedVehicles && (
              <Box className={classes.banner} style={{ marginTop: 12, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', display: 'flex', alignItems: 'center', gap: 8 }}>
                <WarningIcon style={{ fontSize: 18 }} />
                {pt ? `Veículos diferentes nos ficheiros (${distinctVehicles.join(', ')}). Uma rota só aceita o mesmo veículo — seleciona ficheiros do mesmo veículo.` : 'Mixed vehicles.'}
              </Box>
            )}

            {/* Resumo + tabela de pré-visualização */}
            {rows.length > 0 && (
              <>
                <Box className={classes.summaryBar}>
                  <span className={classes.stat} style={{ background: STATUS.ok.bg, color: STATUS.ok.fg, borderColor: STATUS.ok.bd }}><CheckCircleIcon style={{ fontSize: 14 }} /> {okRows.length} OK</span>
                  <span className={classes.stat} style={{ background: STATUS.rever.bg, color: STATUS.rever.fg, borderColor: STATUS.rever.bd }}><WarningIcon style={{ fontSize: 14 }} /> {reverRows.length} {pt ? 'por rever' : 'review'}</span>
                  <span className={classes.stat} style={{ background: STATUS.erro.bg, color: STATUS.erro.fg, borderColor: STATUS.erro.bd }}><ErrorIcon style={{ fontSize: 14 }} /> {erroRows.length} {pt ? 'erro' : 'error'}</span>
                  {distinctVehicles.length === 1 && <span className={classes.stat} style={{ background: C.primarySoft, color: '#075985', borderColor: '#bae6fd' }}><LocalShippingIcon style={{ fontSize: 14 }} /> {distinctVehicles[0]}</span>}
                  <Box style={{ flex: 1 }} />
                  <Button size="small" onClick={() => setRows([])} className={classes.ghostBtn} startIcon={<CloseIcon style={{ fontSize: 14 }} />}>{pt ? 'Limpar' : 'Clear'}</Button>
                </Box>

                <Box className={classes.tableWrap} style={{ overflowX: 'auto' }}>
                  <table className={classes.table}>
                    <thead><tr>
                      <th className={classes.th} style={{ width: 30 }}></th>
                      <th className={classes.th}>{pt ? 'Ficheiro' : 'File'}</th>
                      <th className={classes.th}>{pt ? 'Nº OT (guia)' : 'OT nr (guia)'}</th>
                      <th className={classes.th}>{pt ? 'Obra' : 'Work'}</th>
                      <th className={classes.th}>{pt ? 'Código AT' : 'AT code'}</th>
                      <th className={classes.th}>{pt ? 'Tipo' : 'Type'}</th>
                      <th className={classes.th}>{pt ? 'Estado' : 'Status'}</th>
                      <th className={classes.th} style={{ width: 30 }}></th>
                    </tr></thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const st = STATUS[r.status] || STATUS.erro;
                        return (
                          <tr key={i}>
                            <td className={classes.td} style={{ textAlign: 'center' }}><st.Icon style={{ fontSize: 17, color: st.ic }} /></td>
                            <td className={classes.td} style={{ fontSize: '0.7rem', color: C.dim }}>{r.fileName}</td>
                            <td className={classes.td}><b>{r.guiaNumber || <span style={{ color: C.muted }}>—</span>}</b></td>
                            <td className={classes.td}><b>{r.obraCode || '—'}</b>{r.obraName ? <span style={{ color: C.dim }}> · {r.obraName}</span> : ''}</td>
                            <td className={classes.td}>{r.codigoAT || <span style={{ color: C.muted }}>—</span>}</td>
                            <td className={classes.td}>{r.tipo || <span style={{ color: C.muted }}>—</span>}</td>
                            <td className={classes.td}>
                              <span className={classes.pill} style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                              {r.reason ? <div style={{ fontSize: '0.64rem', color: C.muted, marginTop: 2 }}>{r.reason}</div> : null}
                            </td>
                            <td className={classes.td} style={{ textAlign: 'center' }}>
                              <IconButton size="small" onClick={() => setRows(prev => prev.filter((_, k) => k !== i))}><CloseIcon style={{ fontSize: 14, color: C.muted }} /></IconButton>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.footer}>
        {mode === 'history' ? (
          <Button onClick={onClose} variant="contained" disableElevation className={classes.primaryBtn}>{pt ? 'Fechar' : 'Close'}</Button>
        ) : result ? (
          <Button onClick={onClose} disabled={dispatching} variant="contained" disableElevation className={dispatching ? classes.primaryBtnOff : classes.primaryBtn}>{pt ? 'Concluir' : 'Done'}</Button>
        ) : (
          <>
            <Box style={{ marginRight: 'auto', fontSize: '0.76rem', color: C.dim }}>
              {rows.length > 0
                ? <span><b style={{ color: C.ink }}>{okRows.length}</b> {pt ? 'prontas para criar' : 'ready'}{(reverRows.length + erroRows.length) > 0 ? ` · ${reverRows.length + erroRows.length} ${pt ? 'ignoradas' : 'skipped'}` : ''}</span>
                : <span>{pt ? 'Escolhe os ficheiros das guias para começar' : 'Choose guia files to start'}</span>}
            </Box>
            <Button onClick={onClose} className={classes.ghostBtn}>{pt ? 'Cancelar' : 'Cancel'}</Button>
            <Button onClick={doImport} disabled={creating || okRows.length === 0 || mixedVehicles} variant="contained" disableElevation
              startIcon={creating ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <CheckCircleIcon />}
              className={(okRows.length && !mixedVehicles && !creating) ? classes.primaryBtn : classes.primaryBtnOff}>
              {creating ? (pt ? 'A criar…' : 'Creating…') : (pt ? `Criar ${okRows.length} OT${okRows.length === 1 ? '' : 's'}` : `Create ${okRows.length}`)}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ImportGuiasDialog;
